import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { usePendingAgreementsContext } from 'hooks/context/usePendingAgreementsContext'
import { LegalAgreementDetailsType, LegalAgreementStatus, LegalAgreementTableType } from './types'

export type LegalAgreementsTableProps = {
  agreementsList: LegalAgreementTableType[]
  isLoading: boolean
  onOpenAgreementDetails: (index: number) => void
}

export const LegalAgreementsTable = ({
  agreementsList,
  isLoading,
  onOpenAgreementDetails,
}: LegalAgreementsTableProps) => {
  const { t } = useTranslation()

  const { setShouldRefetch, setShowSingleAgreementId } = usePendingAgreementsContext()

  const columns: DataTableHeaderProps[] = [
    { key: 'agreementName', variant: DataTableCellVariants.TEXT, label: t('legalAgreements.name') },
    { key: 'dateAccepted', variant: DataTableCellVariants.TEXT, label: t('dateAccepted') },
    { key: 'acceptedBy', variant: DataTableCellVariants.TEXT, label: t('acceptedBy') },
    { key: 'status', variant: DataTableCellVariants.TEXT, label: t('status') },
    { key: 'actions', variant: DataTableCellVariants.ACTION, label: '' },
  ]

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!agreementsList?.length) return []

    return agreementsList.map((agreement, index) => {
      const agreementDetails: LegalAgreementDetailsType = agreement?.agreement

      const dateSigned =
        agreement?.status === LegalAgreementStatus?.SIGNED && !!agreement?.lastUpdated
          ? dayjs(agreement?.lastUpdated).format(DateFormat.DD_MMM_YYYY)
          : ''

      const userInitial = agreement?.user?.firstName?.substring(0, 1)
      const signedBy = agreement?.user ? `${userInitial}. ${agreement?.user?.lastName}` : ''

      return {
        id: agreementDetails?.reference,
        testid: agreementDetails?.reference,
        onRowClick: () => onOpenAgreementDetails(index),
        cells: [
          {
            key: 'agreementName',
            variant: DataTableCellVariants.TEXT,
            Text: agreementDetails?.displayName,
          },
          {
            key: 'dateAccepted',
            variant: DataTableCellVariants.TEXT,
            Text: dateSigned,
          },
          {
            key: 'acceptedBy',
            variant: DataTableCellVariants.TEXT,
            Text: signedBy,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={`${agreementDetails?.reference}-status-${agreement?.statusPill?.label}`}
                type={agreement?.statusPill?.state}
                variant="emphasis"
              >
                {agreement?.statusPill?.label}
              </Highlight>,
            ],
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: [
              <DropdownMenu
                key={`context-menu-${agreement?.reference}`}
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key={`details-${agreementDetails?.reference}`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => onOpenAgreementDetails(index)}
                  >
                    {t('legalAgreements.details')}
                  </DropdownMenuItem>,
                  ...(agreement?.status === LegalAgreementStatus.SIGNED
                    ? [
                        <DropdownMenuItem
                          key={`download-${agreementDetails?.reference}`}
                          LeadingIcon={<Icon name="DownloadSimple" />}
                          onClick={() => window.open(agreementDetails?.url)}
                        >
                          {t('download')}
                        </DropdownMenuItem>,
                      ]
                    : [
                        <DropdownMenuItem
                          key={`reviewAndAccept-${agreementDetails?.reference}`}
                          LeadingIcon={<Icon name="Files" />}
                          onClick={() => {
                            setShowSingleAgreementId(agreement?.id)
                            setShouldRefetch(true)
                          }}
                        >
                          {t('reviewAndAccept')}
                        </DropdownMenuItem>,
                      ]),
                ]}
              >
                <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
              </DropdownMenu>,
            ],
          },
        ],
      }
    })
  }, [agreementsList])

  return isLoading ? (
    <RowsSkeletonLoader rows={10} />
  ) : (
    <DataTable headers={columns} data={tableData} />
  )
}
